import React from 'react';
import { ClingConfigMiniRoll } from './ClingConfigMiniRoll';
import { ClingConfigNoteRoll } from './ClingConfigNoteRoll';
import { ClingConfigVoicePanel } from './ClingConfigVoicePanel';
import { ClingSpiritImage } from './ClingSpiritImage';
import {
  CLING_CONFIG_SONGS,
  songSelectionId,
  type ClingConfigSong,
} from '../../lib/melody/clingConfigSongs';
import type { ClingMelodyExercise, ClingPlayMode, SingPitchId } from '../../lib/melody/clingCantaConfig';
import type { ClingExploreLevel } from '../../lib/melody/clingExploreEngine';
import type { ClingNoteSelection } from '../../lib/melody/clingNoteSelectionConfig';
import {
  CLING_CHARACTERS,
  getClingCharacter,
  type ClingCharacterId,
} from '../../lib/melody/clingCharacterConfig';
import type { ClingVisualSettings } from '../../lib/melody/clingVisualConfig';
import type { VoiceTessitura } from '../../lib/melody/clingVoiceConfig';

interface ClingCantaLobbyProps {
  mode: ClingPlayMode;
  melody: ClingMelodyExercise;
  exploreLevel: ClingExploreLevel;
  noteSelection: ClingNoteSelection;
  onTogglePitch: (pitchId: SingPitchId) => void;
  onSelectSong: (song: ClingConfigSong) => void;
  voiceTessitura: VoiceTessitura;
  onVoiceTessitura: (voice: VoiceTessitura) => void;
  characterId: ClingCharacterId;
  onCharacter: (id: ClingCharacterId) => void;
  visual: ClingVisualSettings;
  onVisual: (visual: ClingVisualSettings) => void;
  micReady: boolean;
  micError: string | null;
  simulationMode: boolean;
  livePitch: string | null;
  liveVolume: number;
  liveMidi: number | null;
  micActive: boolean;
  onRequestMic: () => void;
  onStart: () => void;
}

function Stars({ count }: { count: number }) {
  return (
    <span className="cling-config-stars" aria-label={`${count} de 5 estrellas`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={i < count ? 'cling-config-star cling-config-star--on' : 'cling-config-star'}>
          ★
        </span>
      ))}
    </span>
  );
}

export const ClingCantaLobby: React.FC<ClingCantaLobbyProps> = ({
  mode,
  melody,
  exploreLevel,
  noteSelection,
  onTogglePitch,
  onSelectSong,
  voiceTessitura,
  onVoiceTessitura,
  characterId,
  onCharacter,
  visual,
  onVisual,
  micReady,
  micError,
  simulationMode,
  livePitch,
  liveVolume,
  liveMidi,
  micActive,
  onRequestMic,
  onStart,
}) => {
  const selectedSongId = songSelectionId(mode, melody.id, exploreLevel.id);
  const character = getClingCharacter(characterId);

  const setVisual = (patch: Partial<ClingVisualSettings>) => {
    onVisual({ ...visual, ...patch });
  };

  return (
    <div className="cling-config flex flex-col flex-1 min-h-0">
      <header className="cling-config-header shrink-0">
        <span className="cling-config-header-icon" aria-hidden>
          ⚙️
        </span>
        <h2 className="cling-config-header-title junior-display">Menú de Configuración</h2>
      </header>

      <div className="cling-config-grid flex-1 min-h-0 overflow-y-auto eco-scroll-hidden">
        <ClingConfigVoicePanel
          voiceTessitura={voiceTessitura}
          onVoiceTessitura={onVoiceTessitura}
          character={character}
          micReady={micReady}
          micError={micError}
          simulationMode={simulationMode}
          livePitch={livePitch}
          liveVolume={liveVolume}
          liveMidi={liveMidi}
          micActive={micActive}
          onRequestMic={onRequestMic}
        />

        <section className="cling-config-panel cling-config-panel--character">
          <h3 className="cling-config-panel-title">
            <span className="cling-config-panel-icon" aria-hidden>
              🐦
            </span>
            Elección de Personaje
          </h3>
          <div className="cling-config-character-body">
            <div
              className="cling-config-character-preview"
              style={{ background: character.bg }}
            >
              <ClingSpiritImage size="xl" filter={character.filter} />
            </div>
            <div className="cling-config-character-grid">
              {CLING_CHARACTERS.map((c) => {
                const active = c.id === characterId;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`cling-config-character-opt ${active ? 'cling-config-character-opt--active' : ''}`}
                    style={{ background: c.bg }}
                    onClick={() => onCharacter(c.id)}
                    title={c.label}
                  >
                    <ClingSpiritImage size="md" filter={c.filter} />
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="cling-config-panel cling-config-panel--notes">
          <h3 className="cling-config-panel-title">
            <span className="cling-config-panel-icon" aria-hidden>
              🎹
            </span>
            Notas del ejercicio (Piano Roll)
          </h3>
          <ClingConfigNoteRoll selection={noteSelection} onTogglePitch={onTogglePitch} />
        </section>

        <section className="cling-config-panel cling-config-panel--songs">
          <h3 className="cling-config-panel-title">
            <span className="cling-config-panel-icon" aria-hidden>
              🎵
            </span>
            Selección de Canción (Piano Roll)
          </h3>
          <div className="cling-config-songs-scroll">
            {CLING_CONFIG_SONGS.map((song) => {
              const active = selectedSongId === song.id;
              return (
                <button
                  key={song.id}
                  type="button"
                  className={`cling-config-song-card ${active ? 'cling-config-song-card--active' : ''}`}
                  onClick={() => onSelectSong(song)}
                >
                  {song.kind === 'exploracion' && song.sceneBg ? (
                    <div className="cling-config-song-scene" style={{ background: song.sceneBg }}>
                      <span className="cling-config-song-scene-emoji">{song.emoji}</span>
                      <span className="cling-config-song-play">▶</span>
                    </div>
                  ) : song.exercise ? (
                    <ClingConfigMiniRoll exercise={song.exercise} />
                  ) : null}
                  <div className="cling-config-song-meta">
                    <Stars count={song.stars} />
                    <strong className="cling-config-song-title">{song.title}</strong>
                    <span className="cling-config-song-sub">{song.subtitle}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="cling-config-panel cling-config-panel--visual">
          <h3 className="cling-config-panel-title">
            <span className="cling-config-panel-icon" aria-hidden>
              👁
            </span>
            Opciones de Visualización
          </h3>
          <div className="cling-config-visual-body">
            <div className="cling-config-toggle-row">
              <div>
                <strong>Efectos de Brillo</strong>
                <span className="cling-config-toggle-state">{visual.brightnessEffects ? 'On' : 'Off'}</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={visual.brightnessEffects}
                className={`cling-config-switch ${visual.brightnessEffects ? 'cling-config-switch--on' : ''}`}
                onClick={() => setVisual({ brightnessEffects: !visual.brightnessEffects })}
              />
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={visual.brightnessLevel}
              disabled={!visual.brightnessEffects}
              className="cling-config-slider"
              onChange={(e) => setVisual({ brightnessLevel: Number(e.target.value) })}
              aria-label="Intensidad de efectos de brillo"
            />

            <div className="cling-config-toggle-row">
              <div>
                <strong>Guía de Tono Visual</strong>
                <span className="cling-config-toggle-state">{visual.pitchGuide ? 'On' : 'Off'}</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={visual.pitchGuide}
                className={`cling-config-switch ${visual.pitchGuide ? 'cling-config-switch--on' : ''}`}
                onClick={() => setVisual({ pitchGuide: !visual.pitchGuide })}
              />
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={visual.pitchGuideLevel}
              disabled={!visual.pitchGuide}
              className="cling-config-slider"
              onChange={(e) => setVisual({ pitchGuideLevel: Number(e.target.value) })}
              aria-label="Intensidad de guía de tono"
            />
          </div>
        </section>
      </div>

      <footer className="cling-config-footer shrink-0">
        <button
          type="button"
          className="cling-config-start-btn"
          disabled={!micReady}
          onClick={onStart}
        >
          <span aria-hidden>▶</span> Comenzar Aventura Musical
        </button>
      </footer>
    </div>
  );
};

export default ClingCantaLobby;
